import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Card, Col, Progress, Row, Segmented, Space, Table, Tag, message } from 'antd';
import {
  AlertOutlined,
  AuditOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileDoneOutlined,
  LineChartOutlined,
  MessageOutlined,
  RobotOutlined,
  RiseOutlined,
  BookOutlined,
  ThunderboltOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { get } from '../../api/request';
import { API_ENDPOINTS, buildApiUrl } from '../../api/config';
import './Dashboard.css';

interface DashboardSummaryCard {
  key: string;
  title: string;
  value: number;
  suffix?: string;
  prefix?: string;
  trend: string;
  trend_type: 'up' | 'down';
  desc: string;
  precision?: number;
}

interface DashboardPulseItem {
  time: string;
  reports: number;
  chats: number;
  diaries: number;
}

interface DashboardNameValueItem {
  label: string;
  value: number;
  percent?: number;
  color?: string;
}

interface DashboardRiskUser {
  id: string;
  name: string;
  reason: string;
  action: string;
  level: string;
}

interface DashboardAIHistoryItem {
  id: number;
  type: string;
  user: string;
  model: string;
  tokens: string;
  cost: string;
  time: string;
}

interface DashboardAICostSummary {
  today_calls: number;
  avg_cost: string;
  max_single_cost: string;
  failure_rate: string;
}

interface DashboardTodoItem {
  title: string;
  count: number;
  desc: string;
  tone: string;
}

interface DashboardQuickMetric {
  label: string;
  value: string;
  hint: string;
}

interface DashboardBookHeatItem {
  project_id: number;
  title: string;
  author: string;
  category: string;
  read_count: number;
  bookshelf_count: number;
}

interface DashboardOverview {
  generated_at: string;
  summary_cards: DashboardSummaryCard[];
  operation_pulse: DashboardPulseItem[];
  conversion_steps: DashboardNameValueItem[];
  ai_status_distribution: DashboardNameValueItem[];
  risk_users: DashboardRiskUser[];
  ai_cost_summary: DashboardAICostSummary;
  ai_history: DashboardAIHistoryItem[];
  todo_list: DashboardTodoItem[];
  quick_metrics: DashboardQuickMetric[];
  book_heat: DashboardBookHeatItem[];
}

const DEFAULT_OVERVIEW: DashboardOverview = {
  generated_at: new Date().toISOString(),
  summary_cards: [
    {
      key: 'active_users',
      title: '今日活跃创作者',
      value: 0,
      suffix: '人',
      trend: '0.0%',
      trend_type: 'up',
      desc: '按 AI 创作行为去重统计',
    },
    {
      key: 'new_projects',
      title: '今日新增作品',
      value: 0,
      suffix: '部',
      trend: '0.0%',
      trend_type: 'up',
      desc: '累计作品 0 部',
    },
    {
      key: 'ai_chats',
      title: '今日AI创作调用',
      value: 0,
      suffix: '次',
      trend: '0.0%',
      trend_type: 'up',
      desc: '小说生成、设定整理、章节扩写等调用',
    },
    {
      key: 'chapters',
      title: '今日生成章节',
      value: 0,
      suffix: '章',
      trend: '0.0%',
      trend_type: 'up',
      desc: '今日正文约 0 字',
    },
  ],
  operation_pulse: [
    { time: '08:00', reports: 0, chats: 0, diaries: 0 },
    { time: '10:00', reports: 0, chats: 0, diaries: 0 },
    { time: '12:00', reports: 0, chats: 0, diaries: 0 },
    { time: '14:00', reports: 0, chats: 0, diaries: 0 },
    { time: '16:00', reports: 0, chats: 0, diaries: 0 },
    { time: '18:00', reports: 0, chats: 0, diaries: 0 },
    { time: '20:00', reports: 0, chats: 0, diaries: 0 },
  ],
  conversion_steps: [
    { label: '创作者总量', value: 0, percent: 0, color: '#1677ff' },
    { label: '作品总量', value: 0, percent: 0, color: '#13c2c2' },
    { label: '近30天AI创作者', value: 0, percent: 0, color: '#52c41a' },
    { label: '已生成章节', value: 0, percent: 0, color: '#722ed1' },
    { label: '已审稿章节', value: 0, percent: 0, color: '#faad14' },
    { label: '已发布作品', value: 0, percent: 0, color: '#eb2f96' },
  ],
  ai_status_distribution: [
    { label: '成功调用', value: 0, color: '#52c41a' },
    { label: '异常调用', value: 0, color: '#f5222d' },
  ],
  risk_users: [],
  ai_cost_summary: {
    today_calls: 0,
    avg_cost: '¥0.0000',
    max_single_cost: '¥0.0000',
    failure_rate: '0.0%',
  },
  ai_history: [],
  todo_list: [
    { title: 'AI异常待排查', count: 0, desc: '近24小时调用异常及未完成任务', tone: 'danger' },
    { title: '低分章节待修复', count: 0, desc: '审稿分低于80分的章节', tone: 'warning' },
    { title: '发布待处理', count: 0, desc: '已提交但未正式上架的作品', tone: 'info' },
    { title: '低墨水账户', count: 0, desc: '余额低于10墨水的创作者', tone: 'success' },
  ],
  quick_metrics: [
    { label: '平台创作者', value: '0 人', hint: '今日活跃 0 人' },
    { label: '作品沉淀', value: '0 部', hint: '正文约 0 字' },
    { label: '墨水余额', value: '0', hint: '今日AI消耗 0 墨水' },
    { label: 'AI费用', value: '¥0.00', hint: '今日 0 次，均值 0 tokens' },
  ],
  book_heat: [],
};

const summaryMeta: Record<string, { icon: React.ReactNode; color: string }> = {
  active_users: { icon: <UserOutlined />, color: '#1677ff' },
  new_projects: { icon: <BookOutlined />, color: '#13c2c2' },
  ai_chats: { icon: <MessageOutlined />, color: '#722ed1' },
  chapters: { icon: <FileDoneOutlined />, color: '#fa8c16' },
};

const riskUserColumns = [
  {
    title: '对象',
    dataIndex: 'name',
    key: 'name',
  },
  {
    title: '待关注原因',
    dataIndex: 'reason',
    key: 'reason',
    render: (reason: string) => <span className="dashboard-muted">{reason}</span>,
  },
  {
    title: '最近更新',
    dataIndex: 'action',
    key: 'action',
  },
  {
    title: '等级',
    dataIndex: 'level',
    key: 'level',
    render: (level: string) => {
      const color = level === '高' ? 'red' : level === '中' ? 'orange' : 'blue';
      return <Tag color={color}>{level}关注</Tag>;
    },
  },
];

const aiHistoryColumns = [
  {
    title: '类型',
    dataIndex: 'type',
    key: 'type',
    render: (type: string) => (
      <Tag color={type.includes('AI') ? 'purple' : 'cyan'}>{type}</Tag>
    ),
  },
  {
    title: '用户',
    dataIndex: 'user',
    key: 'user',
  },
  {
    title: '模型',
    dataIndex: 'model',
    key: 'model',
    render: (model: string) => <span className="dashboard-code">{model}</span>,
  },
  {
    title: '消耗',
    dataIndex: 'tokens',
    key: 'tokens',
  },
  {
    title: '费用',
    dataIndex: 'cost',
    key: 'cost',
  },
  {
    title: '时间',
    dataIndex: 'time',
    key: 'time',
  },
];

const todoIconMap: Record<string, React.ReactNode> = {
  danger: <AlertOutlined />,
  warning: <AuditOutlined />,
  info: <RobotOutlined />,
  success: <CheckCircleOutlined />,
};

const formatRefreshTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }
  return date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
};

const Dashboard: React.FC = () => {
  const [overview, setOverview] = useState<DashboardOverview>(DEFAULT_OVERVIEW);
  const [heatMetric, setHeatMetric] = useState<'read_count' | 'bookshelf_count'>('read_count');

  const loadOverview = useCallback(async () => {
    try {
      const data = await get<DashboardOverview>(buildApiUrl(API_ENDPOINTS.dashboard.overview));
      setOverview({
        ...DEFAULT_OVERVIEW,
        ...data,
        summary_cards: data.summary_cards?.length ? data.summary_cards : DEFAULT_OVERVIEW.summary_cards,
        operation_pulse: data.operation_pulse?.length ? data.operation_pulse : DEFAULT_OVERVIEW.operation_pulse,
        conversion_steps: data.conversion_steps?.length ? data.conversion_steps : DEFAULT_OVERVIEW.conversion_steps,
        ai_status_distribution: data.ai_status_distribution?.length ? data.ai_status_distribution : DEFAULT_OVERVIEW.ai_status_distribution,
        todo_list: data.todo_list?.length ? data.todo_list : DEFAULT_OVERVIEW.todo_list,
        quick_metrics: data.quick_metrics?.length ? data.quick_metrics : DEFAULT_OVERVIEW.quick_metrics,
      });
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载数据看板失败');
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const maxPulse = useMemo(
    () => Math.max(1, ...overview.operation_pulse.flatMap((item) => [item.reports, item.chats, item.diaries])),
    [overview.operation_pulse]
  );
  const riskUserData = overview.risk_users.map((item) => ({ ...item, key: item.id }));
  const aiHistoryData = overview.ai_history.map((item) => ({ ...item, key: item.id }));
  const bookHeatData = useMemo(
    () => [...overview.book_heat].sort((a, b) => b[heatMetric] - a[heatMetric]),
    [heatMetric, overview.book_heat],
  );
  const maxBookHeat = Math.max(1, ...bookHeatData.map((item) => item[heatMetric]));

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div>
          <h1 className="page-title">数据看板</h1>
          <p className="dashboard-subtitle">
            围绕作品生产、章节质量、墨水资产和AI创作调用的运营总览。
          </p>
        </div>
        <div className="dashboard-header-status">
          <Badge status="processing" text="今日数据持续更新" />
          <span>最近刷新 {formatRefreshTime(overview.generated_at)}</span>
        </div>
      </div>

      <Row gutter={[16, 16]} className="dashboard-summary">
        {overview.summary_cards.map((item) => {
          const meta = summaryMeta[item.key] || summaryMeta.active_users;
          return (
            <Col xs={24} sm={12} xl={6} key={item.key}>
              <Card className="dashboard-stat-card" bordered={false}>
                <div className="dashboard-stat-top">
                  <div className="dashboard-stat-icon" style={{ color: meta.color }}>
                    {meta.icon}
                  </div>
                  <Tag color={item.trend_type === 'up' ? 'green' : 'blue'}>
                    {item.trend_type === 'up' ? <RiseOutlined /> : <LineChartOutlined />}
                    <span>{item.trend}</span>
                  </Tag>
                </div>
                <div className="dashboard-stat-title">{item.title}</div>
                <div className="dashboard-stat-value">
                  {item.prefix && <span className="dashboard-stat-prefix">{item.prefix}</span>}
                  {Number(item.value || 0).toLocaleString('zh-CN', {
                    minimumFractionDigits: item.precision || 0,
                    maximumFractionDigits: item.precision || 0,
                  })}
                  {item.suffix && <span className="dashboard-stat-suffix">{item.suffix}</span>}
                </div>
                <div className="dashboard-stat-desc">{item.desc}</div>
              </Card>
            </Col>
          );
        })}
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={15}>
          <Card
            className="dashboard-panel"
            title={
              <Space>
                <ThunderboltOutlined />
                今日创作节奏
              </Space>
            }
            bordered={false}
          >
            <div className="pulse-chart">
              {overview.operation_pulse.map((item) => (
                <div className="pulse-column" key={item.time}>
                  <div className="pulse-bars">
                    <span
                      className="pulse-bar report"
                      style={{ height: `${(item.reports / maxPulse) * 100}%` }}
                    />
                    <span
                      className="pulse-bar chat"
                      style={{ height: `${(item.chats / maxPulse) * 100}%` }}
                    />
                    <span
                      className="pulse-bar ai-error"
                      style={{ height: `${(item.diaries / maxPulse) * 100}%` }}
                    />
                  </div>
                  <span className="pulse-time">{item.time}</span>
                </div>
              ))}
            </div>
            <div className="pulse-legend">
              <span><i className="legend-dot report" />新增作品</span>
              <span><i className="legend-dot chat" />AI调用</span>
              <span><i className="legend-dot ai-error" />生成章节</span>
            </div>
          </Card>
        </Col>

        <Col xs={24} xl={9}>
          <Card
            className="dashboard-panel"
            title={
              <Space>
                <RobotOutlined />
                AI调用状态
              </Space>
            }
            bordered={false}
          >
            <div className="ai-status-overview">
              {overview.ai_status_distribution.map((item) => (
                <div className="ai-status-item" key={item.label}>
                  <div className="ai-status-row">
                    <span>{item.label}</span>
                    <strong>{item.value}%</strong>
                  </div>
                  <Progress
                    percent={item.value}
                    showInfo={false}
                    strokeColor={item.color}
                    trailColor="#eef1f5"
                  />
                </div>
              ))}
            </div>
          </Card>
        </Col>
      </Row>

      <Card
        className="dashboard-panel dashboard-section"
        title={(
          <Space>
            <BookOutlined />
            小说热度排行
          </Space>
        )}
        extra={(
          <Segmented
            value={heatMetric}
            onChange={(value) => setHeatMetric(value as 'read_count' | 'bookshelf_count')}
            options={[
              { label: '按阅读量', value: 'read_count' },
              { label: '按加入书架量', value: 'bookshelf_count' },
            ]}
          />
        )}
        bordered={false}
      >
        <Table
          rowKey="project_id"
          dataSource={bookHeatData}
          locale={{ emptyText: '暂无已上架小说数据' }}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          scroll={{ x: 760 }}
          size="small"
          columns={[
            {
              title: '排名',
              width: 72,
              render: (_, __, index) => <strong className={index < 3 ? 'dashboard-rank top' : 'dashboard-rank'}>#{index + 1}</strong>,
            },
            {
              title: '小说',
              dataIndex: 'title',
              render: (title, item) => <div><strong>{title}</strong><br /><span className="dashboard-muted">{item.author} · {item.category}</span></div>,
            },
            {
              title: heatMetric === 'read_count' ? '阅读热度' : '书架热度',
              width: 260,
              render: (_, item) => (
                <div className="dashboard-book-heat-bar">
                  <Progress percent={Math.round(item[heatMetric] / maxBookHeat * 100)} showInfo={false} strokeColor={heatMetric === 'read_count' ? '#1677ff' : '#eb2f96'} />
                  <strong>{item[heatMetric].toLocaleString('zh-CN')}</strong>
                </div>
              ),
            },
            { title: '阅读量', dataIndex: 'read_count', width: 120, align: 'right', render: (value) => value.toLocaleString('zh-CN') },
            { title: '加入书架量', dataIndex: 'bookshelf_count', width: 130, align: 'right', render: (value) => value.toLocaleString('zh-CN') },
          ]}
        />
      </Card>

      <Row gutter={[16, 16]} className="dashboard-section">
        <Col xs={24} xl={10}>
          <Card
            className="dashboard-panel"
            title={
              <Space>
                <LineChartOutlined />
                平台核心数据
              </Space>
            }
            bordered={false}
          >
            <div className="conversion-list">
              {overview.conversion_steps.map((step) => (
                <div className="conversion-item" key={step.label}>
                  <div className="conversion-info">
                    <span>{step.label}</span>
                    <strong>{step.value.toLocaleString('zh-CN')}</strong>
                  </div>
                  <Progress
                    percent={step.percent || 0}
                    showInfo={false}
                    strokeColor={step.color}
                    trailColor="#edf1f6"
                  />
                </div>
              ))}
            </div>
          </Card>
        </Col>

        <Col xs={24} xl={14}>
          <Card
            className="dashboard-panel"
            title={
              <Space>
                <AlertOutlined />
                创作待关注
              </Space>
            }
            bordered={false}
          >
            <Table
              columns={riskUserColumns}
              dataSource={riskUserData}
              locale={{ emptyText: '暂无重点提醒' }}
              pagination={false}
              scroll={{ x: 620 }}
              size="small"
              rowClassName="dashboard-table-row"
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} className="dashboard-section">
        <Col xs={24} xl={15}>
          <Card
            className="dashboard-panel"
            title={
              <Space>
                <RobotOutlined />
                AI调用与费用
              </Space>
            }
            bordered={false}
          >
            <div className="ai-cost-strip">
              <div>
                <span>当日调用</span>
                <strong>{overview.ai_cost_summary.today_calls} 次</strong>
              </div>
              <div>
                <span>平均费用</span>
                <strong>{overview.ai_cost_summary.avg_cost}</strong>
              </div>
              <div>
                <span>最高单次</span>
                <strong>{overview.ai_cost_summary.max_single_cost}</strong>
              </div>
              <div>
                <span>失败率</span>
                <strong>{overview.ai_cost_summary.failure_rate}</strong>
              </div>
            </div>
            <Table
              columns={aiHistoryColumns}
              dataSource={aiHistoryData}
              locale={{ emptyText: '暂无AI调用记录' }}
              pagination={false}
              scroll={{ x: 780 }}
              size="small"
              rowClassName="dashboard-table-row"
            />
          </Card>
        </Col>

        <Col xs={24} xl={9}>
          <Card
            className="dashboard-panel"
            title={
              <Space>
                <ClockCircleOutlined />
                待处理事项
              </Space>
            }
            bordered={false}
          >
            <div className="todo-list">
              {overview.todo_list.map((item) => (
                <div className={`todo-item ${item.tone}`} key={item.title}>
                  <div className="todo-icon">{todoIconMap[item.tone] || <CheckCircleOutlined />}</div>
                  <div className="todo-content">
                    <div className="todo-title">
                      <span>{item.title}</span>
                      <strong>{item.count}</strong>
                    </div>
                    <p>{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} className="dashboard-section">
        {overview.quick_metrics.map((item) => (
          <Col xs={24} sm={12} xl={6} key={item.label}>
            <div className="quick-metric">
              <span>{item.label}</span>
              <strong>{item.value}</strong>
              <p>{item.hint}</p>
            </div>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default Dashboard;
